import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const PAW_SVG = `<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true">
  <ellipse cx="16" cy="20" rx="7" ry="6.2"/>
  <ellipse cx="7" cy="13" rx="2.8" ry="3.6"/>
  <ellipse cx="25" cy="13" rx="2.8" ry="3.6"/>
  <ellipse cx="11.5" cy="6.5" rx="2.3" ry="3"/>
  <ellipse cx="20.5" cy="6.5" rx="2.3" ry="3"/>
</svg>`;

export default function GlobalPawCursor() {
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    const cursor = cursorRef.current;
    const layer = layerRef.current;
    if (!cursor || !layer) return;

    const fine = window.matchMedia('(pointer: fine)').matches;
    if (!fine) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const CURSOR_SIZE = 28;
    const PRINT_SIZE = 22;
    const SPAWN_DIST = 34;        // tighter for more reliable trail
    const FIRST_PRINT_DIST = 8;   // quick first print after idle
    const IDLE_MS = 140;          // reset streak after brief pause

    let lastPrintX = 0;
    let lastPrintY = 0;
    let hasPrint = false;
    let side = 1;
    let lastMoveTs = 0;
    let raf = 0;
    let pendingX = 0;
    let pendingY = 0;
    let dirty = false;
    let visible = false;

    const applyCursor = () => {
      dirty = false;
      cursor.style.transform = `translate3d(${pendingX - CURSOR_SIZE / 2}px, ${pendingY - CURSOR_SIZE / 2}px, 0)`;
    };

    const isEditable = (el: Element | null): boolean => {
      if (!el) return false;
      const node = el as HTMLElement;
      if (node.isContentEditable) return true;
      const tag = node.tagName;
      if (tag === 'TEXTAREA') return true;
      if (tag === 'INPUT') {
        const type = (node as HTMLInputElement).type;
        const textTypes = ['text', 'email', 'password', 'search', 'tel', 'url', 'number', 'date', 'time', 'datetime-local', 'month', 'week'];
        return textTypes.includes(type);
      }
      return false;
    };

    const spawnPrint = (x: number, y: number, angleDeg: number) => {
      const el = document.createElement('div');
      const jitter = (Math.random() - 0.5) * 14;
      el.className = 'paw-print';
      el.style.left = `${x - PRINT_SIZE / 2}px`;
      el.style.top = `${y - PRINT_SIZE / 2}px`;
      el.style.width = `${PRINT_SIZE}px`;
      el.style.height = `${PRINT_SIZE}px`;
      el.style.transform = `rotate(${angleDeg + jitter}deg)`;
      el.innerHTML = PAW_SVG;
      layer.appendChild(el);
      requestAnimationFrame(() => {
        el.classList.add('paw-print--fade');
      });
      window.setTimeout(() => { el.remove(); }, 1100);
      if (layer.childElementCount > 40) {
        const first = layer.firstElementChild;
        if (first) first.remove();
      }
    };

    const showCursor = () => {
      if (visible) return;
      visible = true;
      cursor.style.opacity = '1';
      document.documentElement.classList.add('paw-cursor-active');
    };
    const hideCursor = () => {
      if (!visible) return;
      visible = false;
      cursor.style.opacity = '0';
      document.documentElement.classList.remove('paw-cursor-active');
    };

    const onMove = (e: MouseEvent) => {
      const x = e.clientX;
      const y = e.clientY;
      pendingX = x;
      pendingY = y;
      if (!dirty) {
        dirty = true;
        raf = requestAnimationFrame(applyCursor);
      }

      const target = e.target as Element | null;
      const editing = isEditable(target) || (document.activeElement && isEditable(document.activeElement));
      if (editing) {
        hideCursor();
        hasPrint = false;
        return;
      }
      showCursor();

      // interactive scaling
      if (target && (target as HTMLElement).closest && (target as HTMLElement).closest('a, button, [role="button"]')) {
        cursor.classList.add('paw-cursor--small');
      } else {
        cursor.classList.remove('paw-cursor--small');
      }

      const anchorNav = document.documentElement.classList.contains('is-anchor-navigating');
      if (anchorNav) { hasPrint = false; return; }

      if (!reduce) {
        const now = e.timeStamp || performance.now();
        const idle = now - lastMoveTs > IDLE_MS;
        lastMoveTs = now;

        if (!hasPrint || idle) {
          // seed anchor slightly behind current position so first real print appears fast
          lastPrintX = x;
          lastPrintY = y;
          hasPrint = true;
          return;
        }

        const dx = x - lastPrintX;
        const dy = y - lastPrintY;
        const dist = Math.hypot(dx, dy);
        // choose threshold — first print after (re)start uses a smaller distance
        const threshold = SPAWN_DIST;
        if (dist < FIRST_PRINT_DIST) return;

        if (dist >= threshold) {
          const angle = Math.atan2(dy, dx);
          const rotDeg = (angle * 180) / Math.PI + 90;
          // Gap fill: if the jump is much larger than the threshold, place several prints along the path
          const steps = Math.min(6, Math.max(1, Math.floor(dist / threshold)));
          for (let i = 1; i <= steps; i++) {
            const t = i / steps;
            const px = lastPrintX + dx * t;
            const py = lastPrintY + dy * t;
            const perpX = -Math.sin(angle) * 7 * side;
            const perpY = Math.cos(angle) * 7 * side;
            spawnPrint(px + perpX, py + perpY, rotDeg);
            side *= -1;
          }
          lastPrintX = x;
          lastPrintY = y;
        }
      }
    };

    const onLeaveWindow = (e: MouseEvent) => {
      if (!e.relatedTarget && !(e as any).toElement) {
        hideCursor();
        hasPrint = false;
      }
    };

    const onEnterWindow = () => {
      // will re-show on next move
    };

    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseout', onLeaveWindow);
    document.addEventListener('mouseover', onEnterWindow);

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseout', onLeaveWindow);
      document.removeEventListener('mouseover', onEnterWindow);
      if (raf) cancelAnimationFrame(raf);
      layer.innerHTML = '';
      document.documentElement.classList.remove('paw-cursor-active');
    };
  }, []);

  // Clean expired prints on route change
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.innerHTML = '';
  }, [location.pathname]);

  return (
    <>
      <div
        ref={layerRef}
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 2147483646,
          overflow: 'hidden',
        }}
      />
      <div
        ref={cursorRef}
        className="paw-cursor text-cocoa"
        aria-hidden
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 28,
          height: 28,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 2147483647,
          transition: 'opacity 160ms ease, width 180ms ease, height 180ms ease',
          color: '#5A3B1E',
        }}
        dangerouslySetInnerHTML={{ __html: PAW_SVG }}
      />
    </>
  );
}